/**
 * Infer notification kind from filled fields.
 * Priority: media (audio/video) → rich actions → banner image → standard
 */

const AUDIO_EXT = /\.(mp3|wav|ogg|m4a|aac|flac|opus)(\?|#|$)/i;
const VIDEO_EXT = /\.(mp4|webm|mov|m4v|avi|mkv|ogv)(\?|#|$)/i;
const AUDIO_HINT = /(audio|sound|podcast|voice)/i;
const VIDEO_HINT = /(video|watch|stream|youtube|vimeo)/i;

function looksLikeAudio(url) {
  if (!url) return false;
  if (AUDIO_EXT.test(url)) return true;
  if (VIDEO_EXT.test(url)) return false;
  return AUDIO_HINT.test(url) && !VIDEO_HINT.test(url);
}

function looksLikeVideo(url) {
  if (!url) return false;
  if (VIDEO_EXT.test(url)) return true;
  if (AUDIO_EXT.test(url)) return false;
  return VIDEO_HINT.test(url);
}

function hasRichActions(actions) {
  if (!Array.isArray(actions) || actions.length === 0) return false;
  return (
    actions.some(
      (a) =>
        a?.actionType === 'custom' ||
        a?.actionType === 'dismiss' ||
        (a?.actionType === 'open_url' && actions.length > 1),
    ) || actions.length > 1
  );
}

/**
 * @param {{ imageUrl?: string, mediaUrl?: string, actions?: unknown[], url?: string }} fields
 * @returns {'standard'|'banner'|'audio'|'video'|'actionable'}
 */
export function deriveNotificationKind(fields = {}) {
  const mediaUrl = fields.mediaUrl?.trim?.() || fields.mediaUrl || '';
  const imageUrl = fields.imageUrl?.trim?.() || fields.imageUrl || '';

  if (mediaUrl) {
    if (looksLikeAudio(mediaUrl)) return 'audio';
    if (looksLikeVideo(mediaUrl)) return 'video';
    // Unknown media URL → treat as video (poster/imageUrl can still be sent)
    return 'video';
  }

  if (hasRichActions(fields.actions)) return 'actionable';

  if (imageUrl) return 'banner';

  return 'standard';
}

export function describeNotificationKind(kind) {
  switch (kind) {
    case 'banner':
      return 'Banner (image)';
    case 'audio':
      return 'Audio';
    case 'video':
      return 'Video';
    case 'actionable':
      return 'Actionable';
    default:
      return 'Standard';
  }
}
